import { MatchExclusion } from '@libs/src/models/account.model';
import {
  GroupCustomSegment,
  GroupHiddenCustomSegment,
  GroupRole,
} from '@libs/src/models/group.model';
import {
  MatchConfig,
  MatchConfigCustomSegmentConfig,
  MatchSegment,
} from '@libs/src/models/match.interfaces';
import {
  findCustomSegmentNodeInAccountMatchExclusions,
  findRoleInAccountMatchExclusions,
} from '@main-client/src/app/account/account.helpers';
import { generateNodesPerEdge } from '@main-client/src/app/group-custom-segments-settings/group-custom-segments.helper';
import { CustomSegmentsSelections } from '@main-client/src/app/group-intro-selections-form/custom-segment-selections';
import { RoleSelections } from '@main-client/src/app/group-intro-selections-form/role-selections';

import find from 'lodash-es/find';
import isEmpty from 'lodash-es/isEmpty';
import mapValues from 'lodash-es/mapValues';

export interface SelectableOptionMap {
  [option: string]: {
    [optionToMatchWith: string]: boolean;
  };
}

export interface SelectableCustomSegmentsMap {
  [customSegmentId: string]: SelectableOptionMap;
}

export function buildSelectableRolesMap(
  groupRoles: GroupRole[],
  matchSegmentLevel: MatchConfig['match_segment_level'],
  matchSegments: MatchSegment[],
): SelectableOptionMap {
  return groupRoles?.reduce((result, role) => {
    if ('required' === matchSegmentLevel) {
      const matchSegmentForRole = find(
        matchSegments,
        (segment) => role.name === segment.value,
      );
      if (!matchSegmentForRole) {
        return {
          ...result,
          [role.name]: {},
        };
      }
      return {
        ...result,
        [role.name]: matchSegmentForRole.match_with.reduce(
          (matchWithMap, roleNameToMatchWith) => ({
            ...matchWithMap,
            [roleNameToMatchWith]: true,
          }),
          {},
        ),
      };
    }
    return {
      ...result,
      [role.name]: groupRoles.reduce(
        (matchWithMap, groupRole) => ({
          ...matchWithMap,
          [groupRole.name]: true,
        }),
        {},
      ),
    };
  }, {} as any);
}

export function buildSelectableCustomSegmentsMap(
  customSegments: (GroupCustomSegment | GroupHiddenCustomSegment)[],
  customSegmentConfigs: MatchConfigCustomSegmentConfig[],
): SelectableCustomSegmentsMap {
  return (customSegments || [])
    .filter((segment) => segment.is_selectable_for_user_introduction_goals)
    .filter((segment) => !!segment.nodes?.length)
    .reduce((result, segment) => {
      const segmentConfig = find(
        customSegmentConfigs,
        (config) => segment._id === config.custom_segment,
      );
      if ('required' === segmentConfig?.match_type) {
        const segmentMatchWithMap = generateNodesPerEdge({
          edges: segmentConfig.edges,
          nodes: segment.nodes,
        });
        return {
          ...result,
          [segment._id]: mapValues(segmentMatchWithMap, (nodeMatchWith) => {
            const mapping: Record<string, boolean> = {};
            for (const node of nodeMatchWith) {
              mapping[node.name] = true;
            }
            return mapping;
          }),
        };
      }
      const parentMapping: Record<string, Record<string, boolean>> = {};
      for (const parentNode of segment.nodes) {
        const childMapping: Record<string, boolean> = {};
        for (const childNode of segment.nodes) {
          childMapping[childNode.name] = true;
        }
        parentMapping[parentNode.name] = childMapping;
      }
      return {
        ...result,
        [segment._id]: parentMapping,
      };
    }, {} as any);
}

export function buildCustomSegmentSelectionsMap(
  customSegments: GroupCustomSegment[],
  accountMatchExclusions: MatchExclusion[],
): CustomSegmentsSelections {
  return customSegments
    .filter((segment) => !!segment?.nodes?.length)
    .reduce((segmentFormResult: any, segment) => {
      const nodesForm = segment.nodes.reduce((nodeFormResult: any, node) => {
        const customSegmentMatchExclusion =
          findCustomSegmentNodeInAccountMatchExclusions(
            accountMatchExclusions,
            segment._id,
            node.name,
          );
        return {
          ...nodeFormResult,
          [node.name]: isEmpty(customSegmentMatchExclusion),
        };
      }, {});
      return {
        ...segmentFormResult,
        [segment._id]: nodesForm,
      };
    }, {});
}

export function buildRoleSelectionsMap(
  groupRoles: GroupRole[],
  accountMatchExclusions: MatchExclusion[],
): RoleSelections {
  return groupRoles.reduce(
    (result: { [roleName: GroupRole['name']]: boolean }, role: GroupRole) => {
      const roleMatchExclusion = findRoleInAccountMatchExclusions(
        accountMatchExclusions,
        role.name,
      );
      return {
        ...result,
        [role.name]: isEmpty(roleMatchExclusion),
      };
    },
    {},
  );
}
